// src/WidgetPage.tsx
import { useEffect, useRef } from "react";
import { WidgetShell } from "./components/WidgetShell";
import { WidgetContent } from "./components/widget-content";
import { useStudioStore } from "./store/studio-store";
import type { WidgetState } from "./store/studio-store";
import { useAuthStore } from "./store/auth-store";
import { useViewStore } from "./store/view-store";
import { api } from "./lib/api";

export function WidgetPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sendHeight = () => {
      if (!containerRef.current) return;
      const height = containerRef.current.scrollHeight;
      window.parent.postMessage({ type: "WIDGET_RESIZE", height }, "*");
    };

    sendHeight();

    const observer = new ResizeObserver(sendHeight);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const params = new URLSearchParams(window.location.search);
  const studioId = params.get("studioId");
  const layout = params.get("layout");
  const primary = params.get("primary");
  const background = params.get("background");
  const paymentReturn = params.get("payment_return") === "true";

  const setStudioId = useStudioStore((s: WidgetState) => s.setStudioId);
  const loadStudio = useStudioStore((s: WidgetState) => s.loadStudio);
  const status = useStudioStore((s: WidgetState) => s.status);

  const { login } = useAuthStore();
  const { navigate } = useViewStore();

  useEffect(() => {
    const root = document.documentElement;

    if (primary) {
      root.style.setProperty(`--color-primary`, primary);
    }

    if (background) {
      root.style.setProperty("--widget-bg", background);
      document.body.style.backgroundColor = background;
    }
  }, [primary, background]);

  useEffect(() => {
    if (!studioId) return;
    setStudioId(studioId);
    loadStudio(studioId);
  }, [studioId, loadStudio, setStudioId]);

  useEffect(() => {
    if (paymentReturn) {
      navigate("profile");
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete("payment_return");
      const newSearch = cleaned.toString();
      history.replaceState(
        null,
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : ""),
      );
    } else if (layout === "packages") {
      navigate("packages");
    } else if (layout === "private-booking") {
      navigate("private-booking");
    }
  }, [layout, navigate, paymentReturn]);

  useEffect(() => {
    const initialUserId = useAuthStore.getState().user?.id ?? null;
    const initialToken = useAuthStore.getState().token;

    api("/api/widget/auth/me", { skipUnauthorizedLogout: true })
      .then((session) =>
        login({
          user: session.user,
          token: session.token,
        }),
      )
      .catch(() => {
        const currentAuth = useAuthStore.getState();
        const currentUserId = currentAuth.user?.id ?? null;

        if (
          currentUserId === initialUserId &&
          currentAuth.token === initialToken
        ) {
          currentAuth.logout();
        }
      });
  }, [login]);

  // Le conteneur reste monte pour toute la vie du composant : c'est lui que
  // le ResizeObserver observe (effet ci-dessus, deps []), et le detacher pour
  // chaque etat idle/loading/error faisait perdre le suivi de hauteur des la
  // premiere transition, et plantait sur iOS Safari quand un callback en vol
  // visait un noeud deja retire (cf. BOOKZEN-WIDGET-UI-1).
  return (
    <div ref={containerRef}>
      {!studioId ? (
        <div>Missing studio configuration</div>
      ) : status === "loading" ? (
        <div>Loading…</div>
      ) : status === "error" ? (
        <div>Error : Studio not found</div>
      ) : (
        <WidgetShell>
          <WidgetContent />
        </WidgetShell>
      )}
    </div>
  );
}
